import React, { useContext, useEffect, useRef, useState } from "react";
import {
  TextField,
  Typography,
  MenuItem,
  Divider,
  ListSubheader,
} from "@mui/material";
import "bootstrap/dist/css/bootstrap.min.css";
import AddIconn from "../../Images/Addblue.svg";
import DeleteIconn from "../../Images/Deletered.svg";
import { CommonContext } from "../../../context/CommonContext";
import { AuthContext } from "../../../context/AuthContext";
import { referredInitialValues, mapReferredResponse } from "../../../schema/referredInitialValues";
import { apisPath, paths } from "../../../utils/path";
import { authHttp } from "../../../services/commonHttpService";
import { Formik } from "formik";
import { referredSchema } from "../../../schema/referredSchema";
import { useNavigate } from "react-router-dom";
import MedicineSelect from "../../MedicineSelect/MedicineSelect";

const STEP_NUMBER = 5; // API step

const Referred = ({ submitForm, formResult, patientId, currentStep }) => {
  const navigate = useNavigate();
  const { setIsLoading, setAlertState, getMasterData } =
    useContext(CommonContext);
  const { userId } = useContext(AuthContext);

  const formRef = useRef(null);
  const [initialValues, setInitialValues] = useState(referredInitialValues);
  const [isFocus, setIsFocus] = useState(false);

  // local master lists
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [dcccList, setDcccList] = useState([]);

  // Fetch master states on mount (once)
  useEffect(() => {
    const loadStates = async () => {
      const res = await getMasterData(apisPath?.masters?.stateMaster, "states");
      if (res) {
        // API returns objects with state_lgd and state_name
        setStates(res);
      }
    };
    loadStates();
  }, []);

  // Fetch districts when state changes
  const fetchDistricts = async (state_lgd) => {
    if (!state_lgd) {
      setDistricts([]);
      return;
    }
    // districtMaster path contains {state_lgd}
    const path = `${apisPath?.masters?.districtMaster}/${state_lgd}`;

    const res = await getMasterData(path, "districts");
    if (res) setDistricts(res);
  };


  const fetchDccc = async (state_lgd, district_lgd) => {
    if (!state_lgd || !district_lgd) {
      setDcccList([]);
      return;
    }
    // dccMaster
    const path = `${apisPath?.masters?.dccMaster}?state_lgd=${state_lgd}&district_lgd=${district_lgd}`;

    const res = await getMasterData(path, "dccc");

    if (res) setDcccList(res);
  };

  // Fetch saved step data when this step becomes active
  useEffect(() => {
    if (currentStep === 4 && patientId) {
      fetchStepData();
    }
  }, [currentStep, patientId]);

  const fetchStepData = async () => {
    if (!patientId) return;
    setIsLoading(true);
    try {
      const res = await authHttp.get(
        `${apisPath?.getPatientTreatmentStep}/${patientId}/${STEP_NUMBER}`
      );
      // API returns { step: 5, data: { ... } }
      const data = res?.data?.data || res?.data;
      if (data) {
        const mapped = mapReferredResponse(data);
        setInitialValues(mapped);

        // Also pre-load districts/dccc if state/district exist
        if (mapped?.referred_to_state) {
          await fetchDistricts(mapped?.referred_to_state);
        }
        if (mapped?.referred_to_state && mapped?.referred_to_district) {
          await fetchDccc(mapped?.referred_to_state, mapped?.referred_to_district);
        }
      }
    } catch (err) {
      if (err?.response?.status !== 404) {
        setAlertState({
          open: true,
          title: err?.response?.data?.message || "Something went wrong",
          btnTxt: "CLOSE",
          status: "fail",
          isSessionExpire: err?.response?.status === 401,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Parent triggers submit
  useEffect(() => {
    if (submitForm) {
      formRef?.current?.handleSubmit();

      setTimeout(() => {
        if (Object.keys(formRef?.current?.errors || {}).length !== 0) {
          setIsFocus(true);
        }
      }, 100);

      formResult(false);
    }
  }, [submitForm]);

  // show first error and focus
  useEffect(() => {
    const errObj = formRef?.current?.errors || {};
    if (isFocus && Object.keys(errObj).length) {
      const keys = Object.keys(errObj);
      const firstField = window.document.getElementsByName(keys[0])[0];
      if (firstField) firstField.focus();

      setAlertState({
        open: true,
        title: Object.values(errObj)[0],
        btnTxt: "OK",
        status: "fail",
      });

      setIsFocus(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocus, formRef?.current?.errors]);

  // prepare payload for POST
  const refactorDataBeforeSubmit = (values) => {
    // Ensure arrays
    const drug_details = Array.isArray(values?.drug_details)
      ? values?.drug_details.map((d) => ({
        drug_name: d?.drug_name || null,
        dosage: d?.dosage || null,
        final_dose: d?.final_dose || null,
        route_admin: d?.route_admin || null,
      }))
      : [];

    const premedication_details = Array.isArray(values?.premedication_details)
      ? values?.premedication_details.map((p) => ({
        drug_name: p.drug_name || null,
        drug_dose: p.drug_dose || null,
        administration_route: p.administration_route || null,
      }))
      : [];

    return {
      patient_id: Number(patientId),
      user_id: Number(userId),
      facility_id: values?.referred_to_dccc || null,
      referral_date: values?.referral_date || null,
      referred_to_state: values?.referred_to_state || null,
      referred_to_district: values?.referred_to_district || null,
      referred_to_dccc: values?.referred_to_dccc || null,
      referred_to_dcc_name: values?.referred_to_dcc_name || null,

      planned_chemo_cycles: values?.planned_chemo_cycles || null,
      chemo_infusion_rate_duration: values?.chemo_infusion_rate_duration || null,
      chemo_diluent_volume: values?.chemo_diluent_volume || null,
      chemo_timing: values?.chemo_timing || null,
      chemo_delivery_method: values?.chemo_delivery_method || null,
      chemo_notes_special_instructions: values?.chemo_notes_special_instructions || null,

      pre_chemo_hydration_fluid_type: values?.pre_chemo_hydration_fluid_type || null,
      pre_chemo_hydration_fluid_volume: values?.pre_chemo_hydration_fluid_volume || null,
      pre_chemo_hydration_fluid_duration: values?.pre_chemo_hydration_fluid_duration || null,

      post_chemo_hydration_fluid_type: values?.post_chemo_hydration_fluid_type || null,
      post_chemo_hydration_fluid_volume: values?.post_chemo_hydration_fluid_volume || null,
      post_chemo_hydration_fluid_duration: values?.post_chemo_hydration_fluid_duration || null,

      drug_details,
      premedication_details,
      version: 1,
      completed_step: STEP_NUMBER,
    };
  };

  // submit handler
  const submitHandler = async (values) => {
    setIsLoading(true);
    try {
      const payload = refactorDataBeforeSubmit(values);
      const res = await authHttp.post(`${apisPath?.patientTreatmentStep}/${STEP_NUMBER}`, payload);

      if (res?.data?.success || res?.status === 200) {
        setAlertState({
          open: true,
          title: "Referral details saved successfully",
          btnTxt: "CLOSE",
          status: "success",
        });
        formResult(true);

        navigate(`${paths?.user?.commonPoint}${paths?.user?.beneficiaryManagement}`);
      } else {
        setAlertState({
          open: true,
          title: res?.data?.message || "Saved",
          btnTxt: "CLOSE",
          status: "success",
        });
        formResult(true);
      }
    } catch (err) {
      setAlertState({
        open: true,
        title: err?.response?.data?.message || "Something went wrong",
        btnTxt: "CLOSE",
        status: "fail",
        isSessionExpire: err?.response?.status === 401,
      });
      formResult(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      elevation={1}
      className="p-4 mx-auto"
      style={{
        borderRadius: "12px",
        background: "#fff",
        maxWidth: "1200px",
      }}
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={referredSchema}
        onSubmit={submitHandler}
        innerRef={formRef}
      >
        {({ values, errors, touched, handleChange, setFieldValue, handleBlur }) => (
          <>
            <h6 className="header-sub mb-3">
              Instruction for DCCC while referring Patients
            </h6>

            {/* Date */}
            <div className="row mb-3">
              <div className="col-md-4 col-12">
                <TextField
                  label="Date of Referral"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  name="referral_date"
                  value={values?.referral_date}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                // error={touched?.referral_date && Boolean(errors?.referral_date)}
                // helperText={touched?.referral_date && errors?.referral_date}
                />
              </div>
            </div>

            {/* Referred to DCCC - cascading selects */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Referred to DCCC
            </Typography>

            <div className="row mb-3">
              <div className="col-md-4 col-12 mb-3 mb-md-0">
                <TextField
                  fullWidth
                  select
                  label="State"
                  size="small"
                  variant="standard"
                  name="referred_to_state"
                  id="referred_to_state"
                  value={values?.referred_to_state}
                  InputLabelProps={{ shrink: true }}
                  onChange={async (e) => {
                    const v = e.target.value;
                    setFieldValue("referred_to_state", v);
                    setFieldValue("referred_to_district", "");
                    setFieldValue("referred_to_dccc", "");
                    // Fetch districts for this state
                    await fetchDistricts(v);
                  }}
                  error={touched?.referred_to_state && Boolean(errors?.referred_to_state)}
                  helperText={touched?.referred_to_state && errors?.referred_to_state}
                >
                  <MenuItem value="">Select</MenuItem>
                  {states.map((s) => (
                    <MenuItem key={s?.state_lgd} value={s?.state_lgd}>
                      {s?.state_name}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              <div className="col-md-4 col-12 mb-3 mb-md-0">
                <TextField
                  fullWidth
                  select
                  label="District"
                  size="small"
                  variant="standard"
                  name="referred_to_district"
                  value={values?.referred_to_district}
                  onChange={async (e) => {
                    const v = e.target.value;
                    setFieldValue("referred_to_district", v);
                    setFieldValue("referred_to_dccc", "");
                    // fetch dccc list
                    await fetchDccc(values?.referred_to_state, v);
                  }}
                  InputLabelProps={{ shrink: true }}
                  error={touched?.referred_to_district && Boolean(errors?.referred_to_district)}
                  helperText={touched?.referred_to_district && errors?.referred_to_district}
                >
                  <MenuItem value="">Select</MenuItem>
                  {districts.map((d) => (
                    <MenuItem key={d.district_lgd} value={d.district_lgd}>
                      {d.district_name}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  select
                  label="DCCC"
                  size="small"
                  variant="standard"
                  name="referred_to_dccc"
                  value={values?.referred_to_dccc}
                  onChange={(e) => setFieldValue("referred_to_dccc", e.target.value)}
                  error={touched?.referred_to_dccc && Boolean(errors?.referred_to_dccc)}
                  helperText={touched?.referred_to_dccc && errors?.referred_to_dccc}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Select</MenuItem>
                  {dcccList.map((c, i) => (
                    <MenuItem key={c?.facility_id || i} value={c?.facility_id}>
                      {c?.dccc_name.toUpperCase()}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            </div>

            {/* DCC name */}
            {/* <div className="row mb-3">
              <div className="col-md-6 col-12">
                <TextField
                  fullWidth
                  label="DCC Name"
                  name="referred_to_dcc_name"
                  value={values?.referred_to_dcc_name}
                  onChange={handleChange}
                  // error={touched?.referred_to_dcc_name && Boolean(errors?.referred_to_dcc_name)}
                  // helperText={touched?.referred_to_dcc_name && errors?.referred_to_dcc_name}
                />
              </div>
            </div> */}

            <Divider className="my-4" />

            {/* Chemotherapy Drugs for Upcoming Cycles */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Chemotherapy Drugs for Upcoming Cycles
            </Typography>

            {Array.isArray(values?.drug_details) &&
              values?.drug_details.map((drug, idx) => (
                <div key={idx} className="row g-3 align-items-end mb-3">
                  <div className="col-md-3 col-12">
                    <MedicineSelect
                      name={`drug_details[${idx}].drug_name`}
                      value={drug?.drug_name}
                      onChange={(e) =>
                        setFieldValue(`drug_details[${idx}].drug_name`, e.target.value)
                      }
                      label="Drug Name"
                    />
                  </div>

                  <div className="col-md-3 col-12">
                    <TextField
                      label="Dosage (per Kg/flat dose)"
                      name={`drug_details[${idx}].dosage`}
                      value={drug.dosage}
                      onChange={(e) =>
                        setFieldValue(`drug_details[${idx}].dosage`, e.target.value)
                      }
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    // error={
                    //   touched?.drug_details &&
                    //   touched?.drug_details[idx] &&
                    //   errors?.drug_details &&
                    //   errors?.drug_details[idx] &&
                    //   Boolean(errors?.drug_details[idx].dosage)
                    // }
                    // helperText={
                    //   touched?.drug_details &&
                    //   touched?.drug_details[idx] &&
                    //   errors?.drug_details &&
                    //   errors?.drug_details[idx] &&
                    //   errors?.drug_details[idx]?.dosage
                    // }
                    />
                  </div>

                  <div className="col-md-3 col-12">
                    <TextField
                      label="Final Calculated Dose"
                      name={`drug_details[${idx}].final_dose`}
                      value={drug.final_dose}
                      onChange={(e) =>
                        setFieldValue(`drug_details[${idx}].final_dose`, e.target.value)
                      }
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    // error={
                    //   touched?.drug_details &&
                    //   touched?.drug_details[idx] &&
                    //   errors?.drug_details &&
                    //   errors?.drug_details[idx] &&
                    //   Boolean(errors?.drug_details[idx].final_dose)
                    // }
                    // helperText={
                    //   touched?.drug_details &&
                    //   touched?.drug_details[idx] &&
                    //   errors?.drug_details &&
                    //   errors?.drug_details[idx] &&
                    //   errors?.drug_details[idx]?.final_dose
                    // }
                    />
                  </div>

                  <div className="col-md-2 col-10">
                    <TextField
                      select
                      label="Route of Administration"
                      name={`drug_details[${idx}].route_admin`}
                      value={drug?.route_admin}
                      onChange={(e) =>
                        setFieldValue(`drug_details[${idx}].route_admin`, e.target.value)
                      }
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    // error={
                    //   touched?.drug_details &&
                    //   touched?.drug_details[idx] &&
                    //   errors?.drug_details &&
                    //   errors?.drug_details[idx] &&
                    //   Boolean(errors?.drug_details[idx].route_admin)
                    // }
                    // helperText={
                    //   touched?.drug_details &&
                    //   touched?.drug_details[idx] &&
                    //   errors?.drug_details &&
                    //   errors?.drug_details[idx] &&
                    //   errors?.drug_details[idx]?.route_admin
                    // }
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="IV">IV</MenuItem>
                      <MenuItem value="Oral">Oral</MenuItem>
                      <MenuItem value="IM">IM</MenuItem>
                    </TextField>
                  </div>

                  <div className="col-md-1 col-2 d-flex justify-content-center align-items-center gap-2">
                    <button
                      type="button"
                      className="btn border-0 bg-transparent p-0"
                      onClick={() =>
                        setFieldValue("drug_details", [
                          ...values?.drug_details,
                          { drug_name: "", dosage: "", final_dose: "", route_admin: "" },
                        ])
                      }
                    >
                      <img src={AddIconn} alt="Add" width={40} height={40} />
                    </button>

                    {values?.drug_details.length > 1 && (
                      <button
                        type="button"
                        className="btn border-0 bg-transparent p-0"
                        onClick={() =>
                          setFieldValue(
                            "drug_details",
                            values?.drug_details.filter((_, i) => i !== idx)
                          )
                        }
                      >
                        <img src={DeleteIconn} alt="Delete" width={40} height={40} />
                      </button>
                    )}
                  </div>
                </div>
              ))}

            <div className="row mb-3">
              <div className="col-md-6 col-12">
                <TextField
                  fullWidth
                  label="Number of Cycles Planned"
                  type="text"
                  name="planned_chemo_cycles"
                  value={values?.planned_chemo_cycles}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  error={touched?.planned_chemo_cycles && Boolean(errors?.planned_chemo_cycles)}
                  helperText={touched?.planned_chemo_cycles && errors?.planned_chemo_cycles}
                />
              </div>
            </div>

            <Divider className="my-4" />

            {/* Chemotherapy Administration Details */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Chemotherapy Administration Details
            </Typography>

            <div className="row mb-3">
              <div className="col-md-4 col-12 mb-3 mb-md-0">
                <TextField
                  fullWidth
                  label="Infusion solution/dilution"
                  placeholder="e.g.. 30 minutes"
                  name="chemo_infusion_rate_duration"
                  value={values?.chemo_infusion_rate_duration}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                // error={touched?.chemo_infusion_rate_duration && Boolean(errors?.chemo_infusion_rate_duration)}
                // helperText={touched?.chemo_infusion_rate_duration && errors?.chemo_infusion_rate_duration}
                />
              </div>
              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Diluent volume"
                  placeholder="e.g.. Normal saline 100ml"
                  name="chemo_diluent_volume"
                  value={values?.chemo_diluent_volume}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                // error={touched?.chemo_diluent_volume && Boolean(errors?.chemo_diluent_volume)}
                // helperText={touched?.chemo_diluent_volume && errors?.chemo_diluent_volume}
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-4 col-12 mb-3 mb-md-0">
                <TextField
                  fullWidth
                  label="Timing"
                  placeholder="e.g.. Day1, Day8"
                  name="chemo_timing"
                  value={values?.chemo_timing}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                // error={touched?.chemo_timing && Boolean(errors?.chemo_timing)}
                // helperText={touched?.chemo_timing && errors?.chemo_timing}
                />
              </div>
              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Delivery Method"
                  placeholder="Enter method"
                  name="chemo_delivery_method"
                  value={values?.chemo_delivery_method}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-8">
                <TextField
                  fullWidth
                  id="chemo_notes_special_instructions"
                  label="Notes/Special Instructions"
                  placeholder="Enter any special instructions"
                  multiline
                  rows={2}
                  name="chemo_notes_special_instructions"
                  value={values?.chemo_notes_special_instructions}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                // error={touched?.chemo_delivery_method && Boolean(errors?.chemo_delivery_method)}
                // helperText={touched?.chemo_delivery_method && errors?.chemo_delivery_method}
                />
              </div>
            </div>

            <Divider className="my-4" />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Premedications
            </Typography>

            {Array.isArray(values?.premedication_details) &&
              values?.premedication_details.map((pre, idx) => (
                <div key={idx} className="row g-3 align-items-end mb-3">
                  <div className="col-md-4 col-12">
                    <MedicineSelect
                      name={`premedication_details[${idx}].drug_name`}
                      value={pre?.drug_name}
                      onChange={(e) =>
                        setFieldValue(
                          `premedication_details[${idx}].drug_name`,
                          e.target.value
                        )
                      }
                      label="Drug Name"
                    />
                  </div>

                  <div className="col-md-4 col-12">
                    <TextField
                      label="Dose"
                      name={`premedication_details[${idx}].drug_dose`}
                      value={pre.drug_dose}
                      onChange={(e) =>
                        setFieldValue(
                          `premedication_details[${idx}].drug_dose`,
                          e.target.value
                        )
                      }
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    // error={
                    //   touched?.drug_details &&
                    //   touched?.drug_details[idx] &&
                    //   errors?.drug_details &&
                    //   errors?.drug_details[idx] &&
                    //   Boolean(errors?.drug_details[idx].dosage)
                    // }
                    // helperText={
                    //   touched?.drug_details &&
                    //   touched?.drug_details[idx] &&
                    //   errors?.drug_details &&
                    //   errors?.drug_details[idx] &&
                    //   errors?.drug_details[idx]?.dosage
                    // }
                    />
                  </div>

                  <div className="col-md-3 col-10">
                    <TextField
                      select
                      label="Route of Administration"
                      name={`premedication_details[${idx}].administration_route`}
                      value={pre.administration_route}
                      onChange={(e) =>
                        setFieldValue(
                          `premedication_details[${idx}].administration_route`,
                          e.target.value
                        )
                      }
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    // error={
                    //   touched?.drug_details &&
                    //   touched?.drug_details[idx] &&
                    //   errors?.drug_details &&
                    //   errors?.drug_details[idx] &&
                    //   Boolean(errors?.drug_details[idx].route_admin)
                    // }
                    // helperText={
                    //   touched?.drug_details &&
                    //   touched?.drug_details[idx] &&
                    //   errors?.drug_details &&
                    //   errors?.drug_details[idx] &&
                    //   errors?.drug_details[idx]?.route_admin
                    // }
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="IV">IV</MenuItem>
                      <MenuItem value="Oral">Oral</MenuItem>
                      <MenuItem value="IM">IM</MenuItem>
                    </TextField>
                  </div>

                  <div className="col-md-1 col-2 d-flex justify-content-center align-items-center gap-2">
                    <button
                      type="button"
                      className="btn border-0 bg-transparent p-0"
                      onClick={() =>
                        setFieldValue("premedication_details", [
                          ...values?.premedication_details,
                          { drug_name: "", drug_dose: "", administration_route: "" },
                        ])
                      }
                    >
                      <img src={AddIconn} alt="Add" width={40} height={40} />
                    </button>

                    {values?.premedication_details.length > 1 && (
                      <button
                        type="button"
                        className="btn border-0 bg-transparent p-0"
                        onClick={() =>
                          setFieldValue(
                            "premedication_details",
                            values?.premedication_details.filter((_, i) => i !== idx)
                          )
                        }
                      >
                        <img src={DeleteIconn} alt="Delete" width={40} height={40} />
                      </button>
                    )}
                  </div>
                </div>
              ))}

            {/* Hydration plans (simple fields) */}
            <Divider className="my-4" />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Hydration Plan
            </Typography>

            <Typography variant="p" sx={{ fontWeight: 400, marginBottom: "4px" }}>
              Pre-Chemo Hydration
            </Typography>
            <div className="row mb-3 mt-2">
              <div className="col-md-4 col-12 mb-3 mb-md-0">
                <TextField
                  fullWidth
                  label="Fluid Type"
                  name="pre_chemo_hydration_fluid_type"
                  value={values?.pre_chemo_hydration_fluid_type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                // InputLabelProps={{ shrink: true }}
                // error={
                //   touched?.pre_chemo_hydration_fluid_type &&
                //   Boolean(errors?.pre_chemo_hydration_fluid_type)
                // }
                // helperText={
                //   touched?.pre_chemo_hydration_fluid_type &&
                //   errors?.pre_chemo_hydration_fluid_type
                // }
                />
              </div>
              <div className="col-md-4 col-12 mb-3 mb-md-0">
                <TextField
                  fullWidth
                  label="Volume"
                  name="pre_chemo_hydration_fluid_volume"
                  value={values?.pre_chemo_hydration_fluid_volume}
                  onChange={handleChange}
                  onBlur={handleBlur}
                // InputLabelProps={{ shrink: true }}
                // error={
                //   touched?.pre_chemo_hydration_fluid_volume &&
                //   Boolean(errors?.pre_chemo_hydration_fluid_volume)
                // }
                // helperText={
                //   touched?.pre_chemo_hydration_fluid_volume &&
                //   errors?.pre_chemo_hydration_fluid_volume
                // }
                />
              </div>
              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Duration"
                  name="pre_chemo_hydration_fluid_duration"
                  value={values?.pre_chemo_hydration_fluid_duration}
                  onChange={handleChange}
                  onBlur={handleBlur}
                // InputLabelProps={{ shrink: true }}
                // error={
                //   touched?.pre_chemo_hydration_fluid_duration &&
                //   Boolean(errors?.pre_chemo_hydration_fluid_duration)
                // }
                // helperText={
                //   touched?.pre_chemo_hydration_fluid_duration &&
                //   errors?.pre_chemo_hydration_fluid_duration
                // }
                />
              </div>
            </div>

            <div className="row mb-4">
              <Typography variant="p" sx={{ fontWeight: 400 }}>
                Post-Chemo Hydration
              </Typography>
              <div className="col-md-4 col-12 mb-3 mb-md-0 mt-2">
                <TextField
                  fullWidth
                  label="Fluid Type"
                  name="post_chemo_hydration_fluid_type"
                  value={values?.post_chemo_hydration_fluid_type}
                  onChange={handleChange}
                // InputLabelProps={{ shrink: true }}
                // error={touched?.post_chemo_hydration_fluid_type && Boolean(errors?.post_chemo_hydration_fluid_type)}
                // helperText={touched?.post_chemo_hydration_fluid_type && errors?.post_chemo_hydration_fluid_type}
                />
              </div>
              <div className="col-md-4 col-12 mb-3 mb-md-0">
                <TextField
                  fullWidth
                  label="Volume"
                  name="post_chemo_hydration_fluid_volume"
                  value={values?.post_chemo_hydration_fluid_volume}
                  onChange={handleChange}
                // InputLabelProps={{ shrink: true }}
                // error={touched?.post_chemo_hydration_fluid_volume && Boolean(errors?.post_chemo_hydration_fluid_volume)}
                // helperText={touched?.post_chemo_hydration_fluid_volume && errors?.post_chemo_hydration_fluid_volume}
                />
              </div>
              <div className="col-md-4 col-12">
                <TextField
                  fullWidth
                  label="Duration"
                  name="post_chemo_hydration_fluid_duration"
                  value={values?.post_chemo_hydration_fluid_duration}
                  onChange={handleChange}
                // InputLabelProps={{ shrink: true }}
                // error={touched?.post_chemo_hydration_fluid_duration && Boolean(errors?.post_chemo_hydration_fluid_duration)}
                // helperText={touched?.post_chemo_hydration_fluid_duration && errors?.post_chemo_hydration_fluid_duration}
                />
              </div>
            </div>
          </>
        )}
      </Formik>
    </div>
  );
};

export default Referred;